import { isValidPortfolioId } from "../config/portfolioConfig";

function apiBase() {
  const raw = process.env.REACT_APP_PORTFOLIO_API_BASE;
  if (raw === undefined || raw === "") {
    return "/api/";
  }
  return raw.endsWith("/") ? raw : `${raw}/`;
}

/**
 * One URL per portfolioId for future AWS split; swap paths here without touching UI.
 * Today all resolve to the same pattern; later: e.g. `${base}pe/composition`.
 */
export function getPortfolioContextUrl(portfolioId) {
  if (!isValidPortfolioId(portfolioId)) {
    throw new Error("Invalid portfolioId");
  }
  return `${apiBase()}portfolio-context/${portfolioId}`;
}

/** Placeholder slice names until product defines final labels. */
function mockPieComposition(portfolioId) {
  const three = (a, b, c) => [
    { name: a, y: 34 },
    { name: b, y: 33 },
    { name: c, y: 33 },
  ];
  switch (portfolioId) {
    case 1:
      return {
        seriesName: "DS Private Credit — fund mix (mock)",
        data: three("PC Fund I", "PC Fund II", "PC Fund III"),
      };
    case 2:
      return {
        seriesName: "DS Private Equity — sub-strategies (mock)",
        data: three("PE Sub-strategy A", "PE Sub-strategy B", "PE Sub-strategy C"),
      };
    case 3:
      return {
        seriesName: "DS International Equity — sub-strategies (mock)",
        data: three("Intl Eq A", "Intl Eq B", "Intl Eq C"),
      };
    case 4:
      return {
        seriesName: "DS Fixed Income — sub-strategies (mock)",
        data: three("FI Sub-strategy A", "FI Sub-strategy B", "FI Sub-strategy C"),
      };
    case 5:
      return {
        seriesName: "DS Real Estate — sub-strategies (mock)",
        data: three("RE Sub-strategy A", "RE Sub-strategy B", "RE Sub-strategy C"),
      };
    case 6:
      return {
        seriesName: "DS Domestic Equity — sub-strategies (mock)",
        data: three("Dom Eq A", "Dom Eq B", "Dom Eq C"),
      };
    default:
      return { seriesName: "Composition (mock)", data: three("A", "B", "C") };
  }
}

/** Optional QA overlays for static tables — merged into contextData.tables (API may override). */
export function mockTablesOverlay(portfolioId) {
  const book = {
    1: "DS Private Credit",
    2: "DS Private Equity",
    3: "DS International Equity",
    4: "DS Fixed Income",
    5: "DS Real Estate",
    6: "DS Domestic Equity",
  };
  const label = book[portfolioId] || "Portfolio";
  const performanceDelta = (portfolioId - 1) * 0.35;
  const suffix = ` [${label} · id ${portfolioId}]`;
  const firstFundSuffix = `(${label})`;
  return {
    ytdPerformanceByIndex: {
      titleSuffix: suffix,
      firstFundSuffix,
      performanceDelta,
    },
    ytdPerformancePortfolio: {
      titleSuffix: suffix,
      firstFundSuffix,
      performanceDelta: round2(performanceDelta + 0.08),
    },
  };
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function mockPayload(portfolioId) {
  return {
    portfolioId,
    version: "fake-v1",
    loadedAt: new Date().toISOString(),
    summary: { status: "ok", source: "mock" },
    /** Contract for pie: exactly 3 points; same shape expected from real APIs */
    pie: mockPieComposition(portfolioId),
    /** Contract for tables: optional keys ytdPerformanceByIndex | ytdPerformancePortfolio */
    tables: mockTablesOverlay(portfolioId),
  };
}

/**
 * Loads portfolio context. GET per portfolioId (URLs centralised for AWS swap).
 * On failure, returns local mock with `pie` + `tables` overlays.
 * On success, merges `mockTablesOverlay(portfolioId)` with `json.tables` so QA works without backend fields; API values win on key collision.
 */
export async function loadPortfolioContext(portfolioId, signal) {
  if (!isValidPortfolioId(portfolioId)) {
    throw new Error("Invalid portfolioId");
  }

  const url = getPortfolioContextUrl(portfolioId);

  try {
    const res = await fetch(url, { method: "GET", signal });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const json = await res.json();
    const pie =
      json?.pie?.data?.length === 3 ? json.pie : mockPieComposition(portfolioId);
    const tables = {
      ...mockTablesOverlay(portfolioId),
      ...(json.tables && typeof json.tables === "object" ? json.tables : {}),
    };
    return { ...json, pie, tables };
  } catch (e) {
    console.warn(
      "[portfolioApi] Using mock context (network or missing endpoint):",
      e?.message || e
    );
    return mockPayload(portfolioId);
  }
}
